import React, { useState, useEffect, useCallback } from 'react';
import './FichaAnimal.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';
import TabVacunas from './TabVacunas';
import TabCrias from './TabCrias';
import TabPeso from './TabPeso';
import TabInseminacion from './TabInseminacion';

const BASE = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.ANIMALES;

const EMPTY_FORM = {
  caravana: '', nombre: '', sexo: 'HEMBRA', raza: '',
  color: '', colorCaravana: '', clasificacion: '', peso: '', fechaNacimiento: '', estado: 'ACTIVO',
  observaciones: '', madreId: '', padreId: '',
  madreCaravanaInput: '', padreCaravanaInput: ''
};

export default function FichaAnimal({ animalId, onVolver, onVerCria, modal }) {
  const [animal, setAnimal]     = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [tab, setTab]           = useState('datos');
  const [loading, setLoading]   = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError]       = useState('');
  const [exito, setExito]       = useState('');
  const [buscandoMadre, setBuscandoMadre] = useState(false);
  const [buscandoPadre, setBuscandoPadre] = useState(false);
  const [coloresCaravana, setColoresCaravana] = useState([]);
  const esNuevo = !animalId;

  const cargar = useCallback(async () => {
    if (!animalId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${BASE}/${animalId}`);
      const data = await res.json();
      setAnimal(data);
      setForm({
        caravana:        data.caravana        || '',
        nombre:          data.nombre          || '',
        sexo:            data.sexo            || 'HEMBRA',
        raza:            data.raza            || '',
        color:           data.color           || '',
        colorCaravana:   data.colorCaravana   || '',
        clasificacion:   data.clasificacion   || '',
        peso:            data.peso            || '',
        fechaNacimiento: data.fechaNacimiento || '',
        estado:          data.estado          || 'ACTIVO',
        observaciones:   data.observaciones   || '',
        madreId:              data.madreId         || '',
        padreId:              data.padreId         || '',
        madreCaravanaInput:   data.madreCaravana   || '',
        padreCaravanaInput:   data.padreCaravana   || '',
      });
    } catch (e) {
      setError('Error cargando ficha del animal.');
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Cargar colores ya usados para el autocomplete
  useEffect(() => {
    apiFetch(`${BASE}/colores-caravana`)
      .then(r => r.json())
      .then(data => setColoresCaravana(data || []))
      .catch(() => {});

  }, []);

  const handleChange = (e) => {
    const isText = e.target.type === 'text' || e.target.tagName === 'TEXTAREA';
    const value = isText ? e.target.value.toUpperCase() : e.target.value;
    setForm(prev => ({ ...prev, [e.target.name]: value }));
    setError(''); setExito('');
  };

  const buscarPorCaravana = async (caravana, campo) => {
    if (!caravana.trim()) {
      setForm(prev => ({ ...prev, [campo]: '' }));
      return;
    }
    const setLoading = campo === 'madreId' ? setBuscandoMadre : setBuscandoPadre;
    setLoading(true);
    try {
      const res = await apiFetch(`${BASE}/caravana/${encodeURIComponent(caravana.trim())}`);
      if (!res.ok) throw new Error('No encontrado');
      const data = await res.json();
      if (data.id === animalId) throw new Error('No puede ser el mismo animal');
      setForm(prev => ({ ...prev, [campo]: data.id }));
      setExito(`${campo === 'madreId' ? 'Madre' : 'Padre'} encontrado: Caravana ${data.caravana}${data.nombre ? ' — ' + data.nombre : ''}`);
      setTimeout(() => setExito(''), 3000);
    } catch (e) {
      setForm(prev => ({ ...prev, [campo]: '' }));
      setError(`No se encontró animal con caravana "${caravana}"`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.caravana.trim())      return setError('La caravana es obligatoria.');
    if (!form.sexo)                 return setError('El sexo es obligatorio.');
    if (!form.colorCaravana.trim()) return setError('El color de caravana es obligatorio.');

    setGuardando(true);
    setError(''); setExito('');
    try {
      const payload = {
        ...form,
        peso:    form.peso    ? parseFloat(form.peso)    : null,
        madreId: form.madreId ? parseInt(form.madreId)   : null,
        padreId: form.padreId ? parseInt(form.padreId)   : null,
        fechaNacimiento: form.fechaNacimiento || null,
      };

      const url    = esNuevo ? BASE : `${BASE}/${animalId}`;
      const method = esNuevo ? 'POST' : 'PUT';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Error al guardar');
      }

      const saved = await res.json();
      setExito(esNuevo ? 'Animal registrado correctamente.' : 'Datos actualizados correctamente.');
      setAnimal(saved);

      if (esNuevo && onVolver) {
        setTimeout(() => onVolver(), 1200);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const formatFecha = (f) => f ? new Date(f + 'T00:00:00').toLocaleDateString('es-PY') : '-';

  if (loading) return <div className="loading-ficha">Cargando ficha...</div>;

  return (
    <div className="ficha-animal">
      {/* Encabezado */}
      <div className="ficha-header">
        <div className="ficha-header-left">
          {modal && (
            <button className="btn-secondary btn-sm" onClick={onVolver}>← Volver</button>
          )}
          <div>
            <h2>{esNuevo ? 'Nuevo Animal' : `🐄 ${animal?.caravana || ''} ${animal?.nombre ? '— ' + animal.nombre : ''}`}</h2>
            {!esNuevo && animal && (
              <small style={{color:'#888', fontWeight:'normal'}}>ID: {animal.id}</small>
            )}
            {!esNuevo && animal && (
              <span className={`badge badge-${animal.estado?.toLowerCase()}`}>{animal.estado}</span>
            )}
          </div>
        </div>
        {!esNuevo && animal && (
          <div className="ficha-header-meta">
            <span>♂♀ <strong>{animal.sexo}</strong></span>
            {animal.raza && <span>🧬 {animal.raza}</span>}
            {animal.fechaNacimiento && <span>📅 {formatFecha(animal.fechaNacimiento)}</span>}
            {animal.peso && <span>⚖️ {animal.peso} kg</span>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'datos'   ? 'active' : ''}`} onClick={() => setTab('datos')}>
          📋 Datos Generales
        </button>
        {!esNuevo && (
          <>
            <button className={`tab ${tab === 'peso'    ? 'active' : ''}`} onClick={() => setTab('peso')}>
              ⚖️ Peso {animal?.cantidadPesos > 0 ? `(${animal.cantidadPesos})` : ''}
            </button>
            <button className={`tab ${tab === 'vacunas' ? 'active' : ''}`} onClick={() => setTab('vacunas')}>
              💉 Vacunas {animal?.cantidadVacunas > 0 ? `(${animal.cantidadVacunas})` : ''}
            </button>
            <button className={`tab ${tab === 'crias'   ? 'active' : ''}`} onClick={() => setTab('crias')}>
              🐮 Crías {animal?.cantidadCrias > 0 ? `(${animal.cantidadCrias})` : ''}
            </button>
            <button className={`tab ${tab === 'inseminacion' ? 'active' : ''}`} onClick={() => setTab('inseminacion')}>
              🐄 Inseminación {animal?.cantidadInseminaciones > 0 ? `(${animal.cantidadInseminaciones})` : ''}
            </button>
          </>
        )}
      </div>

      {/* Tab: Datos Generales */}
      {tab === 'datos' && (
        <form onSubmit={guardar} className="ficha-form">
          {error  && <div className="alert alert-danger">{error}</div>}
          {exito  && <div className="alert alert-success">{exito}</div>}

          <div className="form-grid">
            <div className="form-group">
              <label>Caravana *</label>
              <input name="caravana" value={form.caravana} onChange={handleChange}
                     placeholder="Ej: 0001" required style={{textTransform:'uppercase'}} />
            </div>
            <div className="form-group">
              <label>Nombre</label>
              <input name="nombre" value={form.nombre} onChange={handleChange}
                     placeholder="Nombre del animal" style={{textTransform:'uppercase'}} />
            </div>
            <div className="form-group">
              <label>Sexo *</label>
              <select name="sexo" value={form.sexo} onChange={handleChange} required>
                <option value="HEMBRA">♀ Hembra</option>
                <option value="MACHO">♂ Macho</option>
              </select>
            </div>
            <div className="form-group">
              <label>Estado</label>
              <select name="estado" value={form.estado} onChange={handleChange}>
                <option value="ACTIVO">Activo</option>
                <option value="VENDIDO">Vendido</option>
                <option value="MUERTO">Muerto</option>
                <option value="TRANSFERIDO">Transferido</option>
              </select>
            </div>
            <div className="form-group">
              <label>Raza</label>
              <input name="raza" value={form.raza} onChange={handleChange}
                     placeholder="Ej: Nelore, Brahman, Angus" style={{textTransform:'uppercase'}} />
            </div>
            <div className="form-group">
              <label>Color</label>
              <input name="color" value={form.color} onChange={handleChange}
                     placeholder="Ej: Blanco, Negro, Marrón" style={{textTransform:'uppercase'}} />
            </div>
            <div className="form-group">
              <label>Color Caravana *</label>
              <input name="colorCaravana" value={form.colorCaravana} onChange={handleChange}
                     placeholder="Ej: Amarillo, Rojo, Azul" required
                     list="lista-colores-caravana" autoComplete="off"
                     style={{textTransform:'uppercase'}} />
              <datalist id="lista-colores-caravana">
                {coloresCaravana.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label>Clasificación</label>
              <select name="clasificacion" value={form.clasificacion} onChange={handleChange}>
                <option value="">— Seleccionar —</option>
                <option value="VACA">Vaca</option>
                <option value="VAQUILLA">Vaquilla</option>
                <option value="NOVILLO">Novillo</option>
                <option value="TORO">Toro</option>
                <option value="DESM_MACHO">Desm. Macho</option>
                <option value="DESM_HEMBRA">Desm. Hembra</option>
                <option value="TERNERO_MACHO">Ternero Macho</option>
                <option value="TERNERO_HEMBRA">Ternero Hembra</option>
              </select>
            </div>
            <div className="form-group">
              <label>Fecha de Nacimiento</label>
              <input type="date" name="fechaNacimiento" value={form.fechaNacimiento} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Caravana Madre</label>
              <div style={{display:'flex', gap:'6px'}}>
                <input
                  name="madreCaravanaInput"
                  value={form.madreCaravanaInput}
                  onChange={handleChange}
                  placeholder="Ej: 101"
                  style={{flex:1, textTransform:'uppercase'}}
                />
                <button type="button"
                  className="btn-secondary btn-sm"
                  disabled={buscandoMadre}
                  onClick={() => buscarPorCaravana(form.madreCaravanaInput, 'madreId')}>
                  {buscandoMadre ? '...' : '🔍'}
                </button>
                {form.madreId && (
                  <button type="button" className="btn-danger btn-sm"
                    onClick={() => setForm(p => ({...p, madreId:'', madreCaravanaInput:''}))}>✕</button>
                )}
              </div>
              {form.madreId && <small style={{color:'green'}}>✓ ID: {form.madreId}</small>}
            </div>
            <div className="form-group">
              <label>Caravana Padre</label>
              <div style={{display:'flex', gap:'6px'}}>
                <input
                  name="padreCaravanaInput"
                  value={form.padreCaravanaInput}
                  onChange={handleChange}
                  placeholder="Ej: 100"
                  style={{flex:1, textTransform:'uppercase'}}
                />
                <button type="button"
                  className="btn-secondary btn-sm"
                  disabled={buscandoPadre}
                  onClick={() => buscarPorCaravana(form.padreCaravanaInput, 'padreId')}>
                  {buscandoPadre ? '...' : '🔍'}
                </button>
                {form.padreId && (
                  <button type="button" className="btn-danger btn-sm"
                    onClick={() => setForm(p => ({...p, padreId:'', padreCaravanaInput:''}))}>✕</button>
                )}
              </div>
              {form.padreId && <small style={{color:'green'}}>✓ ID: {form.padreId}</small>}
            </div>
            {!esNuevo && animal?.madreCaravana && (
              <div className="form-group info-box">
                <label>Madre</label>
                <span>🐄 {animal.madreCaravana} {animal.madreNombre ? '— ' + animal.madreNombre : ''}</span>
              </div>
            )}
            {!esNuevo && animal?.padreCaravana && (
              <div className="form-group info-box">
                <label>Padre</label>
                <span>🐂 {animal.padreCaravana} {animal.padreNombre ? '— ' + animal.padreNombre : ''}</span>
              </div>
            )}
            <div className="form-group full">
              <label>Observaciones</label>
              <textarea name="observaciones" value={form.observaciones} onChange={handleChange}
                        rows={3} placeholder="Notas adicionales sobre el animal..."
                        style={{textTransform:'uppercase'}} />
            </div>
          </div>

          <div className="form-actions">
            {modal && <button type="button" className="btn-secondary" onClick={onVolver}>Cancelar</button>}
            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando ? 'Guardando...' : (esNuevo ? '✓ Registrar Animal' : '✓ Guardar Cambios')}
            </button>
          </div>
        </form>
      )}

      {/* Tab: Peso */}
      {tab === 'peso' && !esNuevo && (
        <TabPeso animalId={animalId} onPesoActualizado={cargar} />
      )}

      {/* Tab: Vacunas */}
      {tab === 'vacunas' && !esNuevo && (
        <TabVacunas animalId={animalId} />
      )}

      {/* Tab: Crías */}
      {tab === 'crias' && !esNuevo && (
        <TabCrias animalId={animalId} onVerCria={onVerCria} />
      )}

      {/* Tab: Inseminación */}
      {tab === 'inseminacion' && !esNuevo && (
        <TabInseminacion animalId={animalId} onInseminacionActualizada={cargar} />
      )}
    </div>
  );
}
